import { useEffect, useRef, useState } from "react";
import { Loader2, CheckCircle2, XCircle, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

export function PaymentSuccess() {
  const [state, setState] = useState("verifying"); // verifying | activating | done | error
  const [message, setMessage] = useState("Confirming your payment with Stripe…");
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("error");
      setMessage("Missing payment session. Please try again from the home page.");
      return;
    }

    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("activating");
          setMessage("Payment confirmed — unlocking your dashboard…");
          await api.post("/payments/activate", { session_id: sessionId });
          setState("done");
          setMessage("You're all set. Redirecting to your dashboard…");
          setTimeout(() => window.location.replace("/?view=syllabus&welcome=1"), 1200);
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("error");
          setMessage("Your payment did not complete. You have not been charged.");
          return;
        }
      } catch (err) {
        // Transient errors are expected while Stripe finalizes; keep polling.
        console.debug("Payment status poll retrying", err);
      }
      if (attempts >= 15) {
        setState("error");
        setMessage("We couldn't confirm your payment in time. If you were charged, refresh this page.");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    // Runs exactly once on mount (guarded by the started ref).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const spinning = state === "verifying" || state === "activating";

  return (
    <div className="min-h-screen bg-navy paper-grain flex items-center justify-center px-4" data-testid="payment-success-page">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        <div className="h-12 w-12 rounded-xl bg-navy flex items-center justify-center mx-auto mb-5">
          <GraduationCap className="h-6 w-6 text-persian-blue" />
        </div>
        {spinning && <Loader2 className="h-10 w-10 text-persian-blue animate-spin mx-auto" />}
        {state === "done" && <CheckCircle2 className="h-12 w-12 text-persian-blue mx-auto" data-testid="payment-success-check" />}
        {state === "error" && <XCircle className="h-12 w-12 text-persian-rose mx-auto" />}
        <h1 className="font-serif text-2xl text-navy mt-4">
          {state === "error" ? "Payment not confirmed" : "Welcome to OCC Premium"}
        </h1>
        <p className="text-slate-600 text-sm mt-2 leading-relaxed">{message}</p>
        {state === "error" && (
          <Button onClick={() => window.location.replace("/")} data-testid="payment-back-home"
            className="mt-6 bg-navy hover:bg-navy-light text-white">Back to home</Button>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen bg-navy paper-grain flex items-center justify-center px-4" data-testid="payment-cancel-page">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        <XCircle className="h-12 w-12 text-persian-rose mx-auto" />
        <h1 className="font-serif text-2xl text-navy mt-4">Checkout cancelled</h1>
        <p className="text-slate-600 text-sm mt-2">No charge was made. You can subscribe anytime.</p>
        <Button onClick={() => window.location.replace("/")} data-testid="payment-cancel-home"
          className="mt-6 bg-persian-rose hover:bg-persian-rosehover text-white">Return to home</Button>
      </div>
    </div>
  );
}
